/**
 * Figma:
 * - Shop - Catalogue: https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11160-41995
 * - Shop - Catalogue (variant): https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=13583-24484
 * - Shop - Store Catalogue: https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11176-43279
 * - Instant Buy - Catalogue: https://www.figma.com/design/NBnn0MEdCHdbMj2LsWQqng/Consumer-App?node-id=11654-23321
 */
import { useI18nContext } from "@i18n/i18n-react";
import { preloadQuery } from "@relay";
import { TabNavLink, TabNavLinks } from "@shadcn/pro-blocks/application/page-headers/page-header-3";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { Input } from "@shadcn/ui/input";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { formatToCurrency } from "@utils/currency";
import { useEffect, useMemo, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import z from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { SncProductCard } from "~/components/SncProductCard";
import { SncProductDrawer } from "~/components/SncProductDrawer";
import { useCart } from "~/contexts/CartContext";
import { shopCatalogueQuery } from "./__generated__/shopCatalogueQuery.graphql";
import { applyTheme, getCurrentTheme } from "@utils/themes";
import { getCategoryName } from "@utils/language";
import { Clock, Search } from "lucide-react";

const CartButton = () => {
	const useCartStore = useCart();
	const totalPrice = useCartStore((state) => state.totalPrice());
	const totalItems = useCartStore((state) => state.totalItems());
	const { hubMeta } = usePreloadedQuery<shopCatalogueQuery>(query, Route.useLoaderData());
	const i18n = useI18nContext().LL["new-ui"];

	if (totalItems === 0) {
		return null;
	}

	return (
		<Button className="w-full shadow-xl" size="lg" asChild>
			<Link to="/$hubMetaName/shop/order-summary" from={Route.fullPath}>
				{i18n.VIEW_ORDER()} <Badge variant="secondary">{formatToCurrency(totalPrice)}</Badge>
			</Link>
		</Button>
	);
};

export const query = graphql`
	query shopCatalogueQuery($hubName: String!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			organization {
				id
				slug
			}
			hub @required(action: THROW) {
				id
				slug
				meta @required(action: THROW) {
					id
					name
					services
					lat
					lng
				}
				sncProducts(filter: { active: true }) {
					nodes {
						id
						stock
						organization {
							id
							name
							logo {
								id
								variant(variant: PUBLIC)
							}
							publicShowcaseImageId {
								id
								variant(variant: PUBLIC)
							}
						}
						product {
							id
							brand {
								id
								name
								organization {
									id
									name
								}
							}
							category {
								id
								name
								i18N
							}
						}
						...SncProductCardFragment
						...SncProductDrawerFragment
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/shop/")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			category: z.string().optional(),
			sncProductId: z.string().optional(),
			orgId: z.string().optional(),
		})
	),
	loader: async (ctx) => {
		return preloadQuery<shopCatalogueQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
		});
	},
});

export default function RouteComponent() {
	const { sncProductId, category: selectedCategory, orgId } = Route.useSearch();
	const { hubMeta } = usePreloadedQuery<shopCatalogueQuery>(query, Route.useLoaderData());
	const hub = hubMeta.hub;
	const useCartStore = useCart();
	const { locale, LL } = useI18nContext();
	const i18n = LL["new-ui"];
	const [searchQuery, setSearchQuery] = useState("");

	// SNC always uses default theme (not thalia)
	if (getCurrentTheme() !== "default") {
		applyTheme("default");
	}

	// Derive unique organizations from sncProducts
	const organizations = useMemo(() => {
		const orgMap = new Map<string, { id: string; name: string; logo: string | null; productCount: number }>();
		for (const sncProduct of hub.sncProducts.nodes) {
			const org = sncProduct.organization;
			if (!orgMap.has(org.id)) {
				orgMap.set(org.id, {
					id: org.id,
					name: org.name,
					logo: org.publicShowcaseImageId?.variant ?? org.logo?.variant ?? null,
					productCount: 0,
				});
			}
			orgMap.get(org.id)!.productCount++;
		}
		return Array.from(orgMap.values());
	}, [hub.sncProducts.nodes]);

	// Filter organizations by search query
	const filteredOrgs = useMemo(() => {
		if (!searchQuery.trim()) return organizations;
		const q = searchQuery.toLowerCase();
		return organizations.filter((org) => org.name.toLowerCase().includes(q));
	}, [organizations, searchQuery]);

	// Products for the selected org
	const orgProducts = useMemo(() => {
		if (!orgId) return [];
		return hub.sncProducts.nodes.filter((p) => p.organization.id === orgId);
	}, [orgId, hub.sncProducts.nodes]);

	// Selected org info
	const selectedOrg = organizations.find((o) => o.id === orgId);

	// Group products by category for the store catalogue view
	const { categories, productsByCategory } = useMemo(() => {
		const products = orgId ? orgProducts : hub.sncProducts.nodes;
		const grouped = new Map<string, (typeof hub.sncProducts.nodes)[number][]>();
		const categoryMap = new Map<string, { id: string; name: string; i18N: unknown }>();

		for (const product of products) {
			const category = product.product.category;
			const categoryId = category?.id ?? "others";

			if (category) {
				categoryMap.set(categoryId, { id: category.id, name: category.name, i18N: category.i18N });
			} else {
				categoryMap.set("others", { id: "others", name: "Others", i18N: null });
			}

			const existing = grouped.get(categoryId) ?? [];
			grouped.set(categoryId, [...existing, product]);
		}

		const categoriesArray = Array.from(categoryMap.values()).sort((a, b) => {
			if (a.id === "others") return 1;
			if (b.id === "others") return -1;
			return a.name.localeCompare(b.name);
		});

		return { categories: categoriesArray, productsByCategory: grouped };
	}, [orgId, orgProducts, hub.sncProducts.nodes]);

	const selectedProduct = hub.sncProducts.nodes.find((p) => p.id === sncProductId);

	// Filter products based on selected category tab
	const filteredProducts = useMemo(() => {
		const products = orgId ? orgProducts : hub.sncProducts.nodes;
		if (!selectedCategory) {
			return products;
		}
		return productsByCategory.get(selectedCategory) ?? [];
	}, [selectedCategory, productsByCategory, orgId, orgProducts, hub.sncProducts.nodes]);

	// Sort products: out of stock at the end
	const sortedProducts = useMemo(() => {
		return [...filteredProducts].sort((a, b) => {
			const aOutOfStock = a.stock === 0;
			const bOutOfStock = b.stock === 0;
			if (aOutOfStock && !bOutOfStock) return 1;
			if (!aOutOfStock && bOutOfStock) return -1;
			return b.stock - a.stock;
		});
	}, [filteredProducts]);

	// Get title for current view
	const currentCategoryTitle = useMemo(() => {
		if (!selectedCategory) {
			return i18n.RECOMMENDED_TITLE();
		}
		const cat = categories.find((c) => c.id === selectedCategory);
		if (!cat || cat.id === "others") {
			return i18n.RECOMMENDED_TITLE();
		}
		return getCategoryName(cat as Parameters<typeof getCategoryName>[0], locale);
	}, [selectedCategory, categories, locale, i18n]);

	// Reset cart when navigating to a different hub
	useEffect(() => {
		const cartItems = useCartStore.getState().items;
		const hasItems = cartItems.length > 0;

		if (hasItems) {
			const currentProductIds = hub.sncProducts.nodes.map((p) => p.id);
			const cartBelongsToCurrentHub = cartItems.every((item) => currentProductIds.includes(item.id));

			if (!cartBelongsToCurrentHub) {
				useCartStore.getState().clearCart();
			}
		}
	}, [hubMeta.name, hub.sncProducts.nodes]);

	const hasItemsInCart = useCartStore((state) => state.totalItems()) > 0;

	const footer = () => {
		if (hasItemsInCart) {
			return <CartButton />;
		}
		return null;
	};

	// Mode 1: Homepage - no orgId selected
	if (!orgId) {
		return (
			<PageContainer footer={footer()}>
				<AppHeader location={{ address: hubMeta.street }} service="SHOP_N_COLLECT" />

				{/* Search input */}
				<div className="px-6 pt-4">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
						<Input
							placeholder={i18n.SEARCH_PRODUCT_OR_STORE()}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9"
						/>
					</div>
				</div>

				{/* Organization list */}
				<div className="flex flex-col gap-3 px-6 pt-4 pb-6">
					{filteredOrgs.map((org) => (
						<Link key={org.id} from="/$hubMetaName/shop/" search={{ orgId: org.id }} className="block">
							<div className="flex items-center gap-4 p-4 rounded-xl border bg-white hover:bg-muted/50 transition-colors">
								<img
									src={org.logo ?? "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400"}
									alt={org.name}
									className="size-16 rounded-lg object-cover shrink-0"
								/>
								<div className="flex flex-col gap-1 min-w-0">
									<p className="text-lg font-breit font-semibold truncate">{org.name}</p>
									<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
										<Clock className="size-4 shrink-0" />
										<span>{i18n.DELIVERY_WITHIN_HOURS({ hours: 24 })}</span>
									</div>
								</div>
							</div>
						</Link>
					))}
				</div>

				{selectedProduct && <SncProductDrawer key={sncProductId} hubId={hub.id} product={selectedProduct} />}
			</PageContainer>
		);
	}

	// Mode 2: Store catalogue - orgId is set
	return (
		<PageContainer footer={footer()}>
			<AppHeader location={{ address: hubMeta.street }} service="SHOP_N_COLLECT" />

			{/* Org header */}
			{selectedOrg && (
				<div className="px-6 pt-4">
					<img
						src={selectedOrg.logo ?? "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400"}
						alt={selectedOrg.name}
						className="h-40 w-full object-cover rounded-xl"
					/>
					<h1 className="text-2xl font-breit mt-4">{selectedOrg.name}</h1>
				</div>
			)}

			{/* Category tabs */}
			<TabNavLinks className="mb-6">
				<TabNavLink label={i18n.ALL_PRODUCTS()} from="/$hubMetaName/shop/" search={{ orgId }} />
				<TabNavLink label={i18n.RECOMMENDED()} from="/$hubMetaName/shop/" search={{ orgId, category: "recommended" }} />
				{categories.map((category) => {
					const categoryLabel =
						category.id === "others"
							? i18n.RECOMMENDED_TITLE()
							: getCategoryName(category as Parameters<typeof getCategoryName>[0], locale);

					return <TabNavLink key={category.id} label={categoryLabel} from="/$hubMetaName/shop/" search={{ orgId, category: category.id }} />;
				})}
			</TabNavLinks>

			{/* Products list - vertical layout */}
			<h2 className="text-2xl text-neutral-900 font-breit pt-4 px-6">{currentCategoryTitle}</h2>
			<div className="flex flex-col gap-4 px-6 pb-6">
				{sortedProducts.map((product) => (
					<SncProductCard key={product.id} product={product} />
				))}
			</div>

			{selectedProduct && <SncProductDrawer key={sncProductId} hubId={hub.id} product={selectedProduct} />}
		</PageContainer>
	);
}
